import _get from 'lodash/get';
import _omit from 'lodash/omit';
import { LAYOUT_ENTITIES, LAYOUT_FRONTSPACES } from '@bedrock/fido-sdk';

export const getBlockInfo = (state, blockId, parentId = undefined) => {
  if (!state || !blockId) {
    return null;
  }

  const { requestedEntityId, requestedEntityType, currentSectionCode } = state;
  const blocks = _get(state, [currentSectionCode, requestedEntityType, requestedEntityId, 'blocks'], []);
  const blockToFind = parentId || blockId;
  const blockIndex = blocks.findIndex(({ id }) => id === blockToFind);

  if (blockIndex < 0) {
    return null;
  }

  const target = parentId
    ? _get(blocks[blockIndex], 'alternativeContent.concurrentBlocks', []).find(({ id }) => id === blockId)
    : blocks[blockIndex];

  if (!target) {
    return null;
  }

  const path = [currentSectionCode, requestedEntityType, requestedEntityId, `blocks[${blockIndex}]`];

  if (parentId) {
    const targetBlockIndex = blocks[blockIndex].alternativeContent.concurrentBlocks.indexOf(target);
    path.push('alternativeContent', `concurrentBlocks[${targetBlockIndex}]`);
  }

  return [path, target];
};

/**
 * How many layouts stay in the cache.
 *
 * The cache is an optimisation, never a source of truth: `fetchLayout` goes to the network on every navigation,
 * so a miss costs a skeleton while the request is in flight and nothing more. The limit therefore trades instant
 * back-navigation depth against retained memory — each entry holds every block of a layout, including the items
 * pagination has appended to it, which is what made the cache grow for the whole session.
 */
export const LAYOUT_CACHE_LIMIT = 8;

export const makeLayoutCacheKey = (sectionCode, entityType, entityId) => `${sectionCode}|${entityType}|${entityId}`;

const splitLayoutCacheKey = key => key.split('|');

const hasLayout = (state, key) => {
  const [sectionCode, entityType, entityId] = splitLayoutCacheKey(key);

  return _get(state, [sectionCode, entityType, entityId]) !== undefined;
};

/**
 * Layouts that survive eviction whatever their age
 */
const isPinnedLayout = (state, key) => {
  const [sectionCode, entityType, entityId] = splitLayoutCacheKey(key);

  // The search screen keeps its query inside its own cached layout, and fetchLayout reads it back to restore the
  // query even once search has left the navigation stack. Dropping it would silently lose the last search.
  if (entityType === LAYOUT_ENTITIES.frontspace && entityId === LAYOUT_FRONTSPACES.search) {
    return true;
  }

  // Whatever is on screen, or on its way to it
  return key === makeLayoutCacheKey(sectionCode, state.requestedEntityType, state.requestedEntityId);
};

const dropLayout = (state, key) => {
  const [sectionCode, entityType, entityId] = splitLayoutCacheKey(key);
  const bucket = _get(state, [sectionCode, entityType]);

  if (!bucket || bucket[entityId] === undefined) {
    return state;
  }

  return {
    ...state,
    [sectionCode]: {
      ...state[sectionCode],
      [entityType]: _omit(bucket, entityId),
    },
  };
};

/**
 * Moves a layout to the most recently used end of the order.
 *
 * Every navigation refetches, so the order in which layouts are written is also the order in which they are
 * looked at — which is what makes a write-ordered list a genuine LRU here.
 *
 * @param {Array} order the current order, oldest first
 * @param {string} key the layout that has just been written
 * @returns {Array} the next order
 */
export const touchLayoutCache = (order = [], key) => [...order.filter(entry => entry !== key), key];

/**
 * Trims the cache to LAYOUT_CACHE_LIMIT, keeping pinned layouts however old they are and forgetting keys whose
 * layout is already gone, so the order heals itself after any CLEAR_CACHE
 *
 * @param {Object} state the layout slice, already holding the newly fetched layout
 * @param {Array} order the order after touching the new layout
 * @returns {Object} the slice with the overflow evicted and `cacheOrder` updated
 */
export const applyLayoutCacheLimit = (state, order = []) => {
  const kept = [];
  const evicted = [];

  // Newest first, so the survivors are the most recent ones
  for (let i = order.length - 1; i >= 0; i -= 1) {
    const key = order[i];

    if (!hasLayout(state, key)) {
      // Already cleared by something else, just forget the key
      // eslint-disable-next-line no-continue
      continue;
    }

    if (kept.length < LAYOUT_CACHE_LIMIT || isPinnedLayout(state, key)) {
      kept.unshift(key);
    } else {
      evicted.push(key);
    }
  }

  return {
    ...evicted.reduce(dropLayout, state),
    cacheOrder: kept,
  };
};
